import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import 'react-toastify';
import LayerContextMenu from './LayerContextMenu';
import { Edit } from 'lucide-react';
import { getCropRect, getDesignCanvas } from '../utils/reframe';

const VideoPreview = forwardRef(({
    layers,
    dynamicCaptions,
    currentTime,
    isPlaying,
    aspectRatio = '16:9',
    selectedClipId,
    onSelectClip,
    onUpdateClip,
    onUpdateLayerTransform,
    onPlay,
    onPause,
    onStop,
    onSeek,
    overlayRatio = null,
    exportCropOffsets = {},
    activeCropFormat = null,
    onUpdateCropOffset,
    onUpdateDynamicCaptions,
    onOpenCaptionSettings,
    isCaptionSelected,
    onSelectCaption,
    isLoading = false,
    loadingProgress = 0,
    loadingMessage = '',
    onEditText,
    isExportingMode = false,
    exportProgress = 0,
    exportMessage = ''
}, ref) => {
    const canvasRef = useRef(null);
    const getCaptionsRectRef = useRef(null);
    const containerRef = useRef(null);
    const mediaRefs = useRef({});
    useRef(new Set()); // Track failed media IDs
    const [canvasSize, setCanvasSize] = useState(() => getDesignCanvas(aspectRatio));
    const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

    const [isDragging, setIsDragging] = useState(false);
    const [dragMode, setDragMode] = useState(null);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [initialTransform, setInitialTransform] = useState({ x: 0.5, y: 0.5, scale: 1 });
    const hasDragged = useRef(false);

    const [isExporting, ] = useState(false);
    const isExportingRef = useRef(false);

    // Internal loop state
    const requestRef = useRef();
    useRef(null);

    // Audio Context
    const layoutCacheRef = useRef({});
    const pendingTransformRef = useRef(null); // Store latest transform during drag for smooth rAF updates

    // Dynamic captions are rendered separately from the timeline clips, so
    // keep them in sync with the captions layer's visibility and existence.
    const captionsLayer = layers.find(layer =>
        layer.id === 'captions-layer' ||
        (layer.type || '').toLowerCase() === 'captions' ||
        ['captions', 'subtitle', 'subtitles'].includes((layer.name || '').toLowerCase())
    );
    const shouldRenderCaptions = Boolean(
        dynamicCaptions && captionsLayer && captionsLayer.visible !== false
    );


    // Update canvas size when aspect ratio changes
    useEffect(() => {
        setCanvasSize(getDesignCanvas(aspectRatio));
    }, [aspectRatio]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, []);

    // Update display size when container or aspect ratio changes
    useEffect(() => {
        const updateSize = () => {
            const container = containerRef.current;
            if (!container) return;

            const { width: containerW, height: containerH } = container.getBoundingClientRect();
            if (containerW === 0 || containerH === 0) return;

            // Subtract padding (py-8 = 32px * 2 = 64px, px-4 = 16px * 2 = 32px)
            const paddingX = 32;
            const paddingY = 64;

            const availW = Math.max(0, containerW - paddingX);
            const availH = Math.max(0, containerH - paddingY);

            const [aspectW, aspectH] = aspectRatio.split(':').map(Number);
            const targetRatio = aspectW / aspectH;

            let w, h;
            if (availW / availH > targetRatio) {
                h = availH;
                w = h * targetRatio;
            } else {
                w = availW;
                h = w / targetRatio;
            }

            setDisplaySize({ width: w, height: h });
        };

        updateSize();
        const resizeObserver = new ResizeObserver(updateSize);
        if (containerRef.current) resizeObserver.observe(containerRef.current);

        window.addEventListener('resize', updateSize);
        return () => {
            window.removeEventListener('resize', updateSize);
            resizeObserver.disconnect();
        };
    }, [aspectRatio]);

    // Helper to get dimensions of first video/image
    const getContentDimensions = () => {
        // Find first visible video layer
        for (const layer of layers) {
            const type = (layer.type || "").toLowerCase();
            if (layer.visible && (type === 'video' || type === 'image')) {
                // Check if we have a loaded media element
                const clip = layer.clips[0];
                if (clip && mediaRefs.current[clip.id]) {
                    const el = mediaRefs.current[clip.id];
                    const w = el.videoWidth || el.naturalWidth;
                    const h = el.videoHeight || el.naturalHeight;
                    if (w && h) return { width: w, height: h };
                }
            }
        }
        return null;
    };

    useImperativeHandle(ref, () => ({
        getContentDimensions,
        // Read the canvas that is actually displayed, before export UI updates.
        getExportFrame: () => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const size = { width: canvas.width, height: canvas.height };
            const ratio = overlayRatio || aspectRatio;
            return { canvas: size, cropRect: getCropRect(size, ratio, exportCropOffsets[ratio]) };
        },
    }));


    // Audio Context for export mixing
    // (Already defined above)

    // Interaction State - REMOVED DUPLICATES
    // Sync ref with state
    useEffect(() => {
        isExportingRef.current = isExporting;
    }, [isExporting]);

    const currentTimeRef = useRef(currentTime);

    // Keep ref updated to avoid stale closures during export
    useEffect(() => {
        currentTimeRef.current = currentTime;
    }, [currentTime]);

    // Unified render function for both preview and export
    // Unified render function for both preview and export


    // --- Playback Synchronization ---
    useEffect(() => {
        // Skip sync during export (handled by renderFrame/worker)
        if (isExportingRef.current) return;

        const syncMedia = () => {
            Object.entries(mediaRefs.current).forEach(([id, element]) => {
                if (!element) return;

                // Find which clip/layer this element belongs to
                let clip = null;
                let layer = null;

                // Helper to find clip/layer
                for (const l of layers) {
                    const c = l.clips.find(clip => clip.id === id);
                    if (c) {
                        clip = c;
                        layer = l;
                        break;
                    }
                }

                if (!clip || !layer || !layer.visible) {
                    if (element.pause && !element.paused) element.pause();
                    return;
                }

                const clipStart = clip.startTime;
                const clipEnd = clip.startTime + clip.duration;
                const isActive = currentTime >= clipStart && currentTime < clipEnd;

                // Sync Mute State
                element.muted = layer.muted || (clip.muted === true);
                // Sync Volume (use layer volume, defaulting to 1)
                element.volume = layer.volume !== undefined ? layer.volume : 1.0;

                if (isActive) {
                    // Calculate where the video head should be
                    // (Current Timeline Time - Clip Start Time) + Trim Start Offset
                    const targetTime = (currentTime - clipStart) + (clip.trimStart || 0);

                    // 1. Sync Time (Drift Correction)
                    // If the element's time is significantly off (>0.3s), snap it back.
                    // We allow small drift to avoid stuttering audio/video.
                    // IMPORTANT: Only correct drift if we are not actively buffering/seeking
                    if (!element.seeking && Math.abs(element.currentTime - targetTime) > 0.3) {
                        element.currentTime = targetTime;
                    }

                    // 2. Play/Pause Control
                    if (isPlaying) {
                        // If it should be playing but is paused, play it
                        if (element.play && element.paused) {
                            const playPromise = element.play();
                            if (playPromise !== undefined) {
                                playPromise.catch(error => {
                                    // Auto-play was prevented
                                    // console.warn("Auto-play prevented", error);
                                });
                            }
                        }
                    } else {
                        // If it should be paused (timeline paused), pause it
                        if (element.pause && !element.paused) element.pause();
                    }
                } else {
                    // Clip is not active at this time
                    if (element.pause && !element.paused) element.pause();
                }
            });
        };

        syncMedia();

    }, [isPlaying, currentTime, layers, isExporting]);

    // Effect to handle export stop
    useEffect(() => {
        if (window.exportRecorder && !isPlaying) {
            setTimeout(() => {
                if (window.exportRecorder) window.exportRecorder.stop();
            }, 500);
        }
    }, [isPlaying]);

    // Helper to sync audio volumes (DOM + WebAudio)


    // Sync Audio Volume
    useEffect(() => {
const syncAudioVolumes = () => {
        layers.forEach(layer => {
            const type = (layer.type || "").toLowerCase();
            if (type === 'video' || type === 'audio') {
                const volume = layer.muted ? 0 : (layer.volume !== undefined ? layer.volume : 1);
                layer.clips.forEach(clip => {
                    const element = mediaRefs.current[clip.id];
                    if (element) {
                        // 1. Force unmute for DOM playback so we can control volume via element.volume
                        // If volume is 0, we can mute the element as well to be safe
                        if (volume > 0) element.muted = false;
                        element.volume = volume;
                        if (volume === 0) element.muted = true;
                    }
                });
            }
        });
    };

        syncAudioVolumes();
    }, [layers]);


    // Render loop for preview (RequestAnimationFrame)
    useEffect(() => {
const renderFrame = (time) => {

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) {
            console.warn("Canvas or Context missing");
            return;
        }

        // Clear canvas
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // --- REFERENCE FRAME LOGIC ---
        // Calculate the "Base Dimensions" that the user was designing in (e.g. 9:16 Preview)
        // If exporting, we use the Base Aspect Ratio to reconstruct this Virtual Frame.
        // If not exporting, the Base Dimensions ARE the current Canvas Dimensions.
        let baseW = canvas.width;
        let baseH = canvas.height;

        // Note: isExporting logic was removed because rendering now happens server-side.
        // baseW and baseH match the canvas dimensions for preview purposes.

        let hasVideo = false;

        // Apply Global Crop Offset (Inverse) for Export/Native View
        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeRatioKey = overlayRatio || aspectRatio;
        const activeOffset = isNativeView ? (exportCropOffsets[activeRatioKey] || { x: 0, y: 0 }) : { x: 0, y: 0 };

        ctx.save();

        // 1. Center the Virtual Base Frame on the Actual Canvas
        if (Math.abs(baseW - canvas.width) > 1 || Math.abs(baseH - canvas.height) > 1) {
            ctx.translate((canvas.width - baseW) / 2, (canvas.height - baseH) / 2);
        }

        // 2. Apply User Crop Offset (Pan the camera)
        if (activeOffset.x !== 0 || activeOffset.y !== 0) {
            ctx.translate(-activeOffset.x * baseW, -activeOffset.y * baseH);
        }

        // Draw video and text layers
        layers.forEach(layer => {
            if (!layer.visible) return;

            layer.clips.forEach(clip => {
                if (clip.visible === false) return;

                const clipStart = clip.startTime;
                const clipEnd = clip.startTime + clip.duration;

                if (time >= clipStart && time < clipEnd) {
                    const type = (layer.type || "").toLowerCase();
                    const transform = (type === 'text' && layer.sharedTransform?.enabled)
                        ? layer.sharedTransform
                        : (clip.transform || { x: 0.5, y: 0.5, scale: 1 });

                    if (type === 'video' || type === 'image') {
                        const element = mediaRefs.current[clip.id];
                        if (!element) return;
                        const isReady = type === 'video' ? (element.readyState >= 1) : (element.complete && element.naturalWidth > 0);
                        if (!isReady) return;

                        // NOTE: Seeking during export is handled exclusively by the worker tick
                        // handler, which uses a 2.0s threshold + element.seeking guard.
                        // The old seek here compared against the wrong variable and never executed.

                        const elWidth = element.videoWidth || element.naturalWidth;
                        const elHeight = element.videoHeight || element.naturalHeight;

                        const hRatio = baseW / elWidth;
                        const vRatio = baseH / elHeight;
                        const ratio = Math.max(hRatio, vRatio) * transform.scale;

                        const drawWidth = elWidth * ratio;
                        const drawHeight = elHeight * ratio;

                        const drawX = (transform.x * baseW) - (drawWidth / 2);
                        const drawY = (transform.y * baseH) - (drawHeight / 2);

                        ctx.save();
                        if (transform.opacity !== undefined) ctx.globalAlpha = transform.opacity;
                        if (transform.rotation) {
                            ctx.translate(transform.x * baseW, transform.y * baseH);
                            ctx.rotate((transform.rotation * Math.PI) / 180);
                            ctx.translate(-(transform.x * baseW), -(transform.y * baseH));
                        }
                        ctx.drawImage(element, 0, 0, elWidth, elHeight, drawX, drawY, drawWidth, drawHeight);
                        ctx.restore();
                        hasVideo = true;
                    } else if ((layer.type || "").toLowerCase() === 'text') {
                        const useShared = layer.sharedTransform?.enabled;
                        const styleSource = (useShared && layer.sharedTransform?.style) ? layer.sharedTransform.style : (clip.style || {});
                        const text = clip.text || 'Text';
                        const style = { fontSize: 20, color: '#ffffff', fontFamily: 'Arial', backgroundColor: 'transparent', ...styleSource };
                        const animationSource = (useShared && layer.sharedTransform?.animation) ? layer.sharedTransform.animation : (clip.animation || { type: 'none', duration: 1 });
                        const animDuration = animationSource.duration || 1;
                        const timeSinceStart = time - clipStart;
                        const progress = Math.min(1, Math.max(0, timeSinceStart / animDuration));
                        const easeOut = 1 - Math.pow(1 - progress, 3);

                        ctx.save();
                        const drawX = transform.x * baseW;
                        const drawY = transform.y * baseH;

                        if (animationSource.type === 'fade') ctx.globalAlpha = easeOut;
                        else if (animationSource.type === 'slide-up') { ctx.globalAlpha = easeOut; ctx.translate(0, 50 * (1 - easeOut)); }
                        else if (animationSource.type === 'slide-down') { ctx.globalAlpha = easeOut; ctx.translate(0, -50 * (1 - easeOut)); }
                        else if (animationSource.type === 'pop') { ctx.translate(drawX, drawY); ctx.scale(easeOut, easeOut); ctx.translate(-drawX, -drawY); }

                        ctx.font = `${style.fontSize * transform.scale}px ${style.fontFamily}`;
                        ctx.fillStyle = style.color;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        const maxWidth = (transform.maxWidth || 0.9) * baseW;
                        const lineHeight = style.fontSize * transform.scale * 1.2;
                        const paragraphs = text.split('\n');
                        let lines = [];
                        paragraphs.forEach(paragraph => {
                            const words = paragraph.split(' ');
                            let currentLine = words[0];
                            for (let i = 1; i < words.length; i++) {
                                if (ctx.measureText(currentLine + " " + words[i]).width < maxWidth) currentLine += " " + words[i];
                                else { lines.push(currentLine); currentLine = words[i]; }
                            }
                            lines.push(currentLine);
                        });
                        const totalHeight = lines.length * lineHeight;
                        const startY = drawY - (totalHeight / 2) + (lineHeight / 2);

                        if (style.backgroundColor && style.backgroundColor !== 'transparent') {
                            const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
                            const padding = 10;
                            ctx.fillStyle = style.backgroundColor;
                            ctx.fillRect(drawX - (maxLineWidth / 2) - padding, drawY - (totalHeight / 2) - padding, maxLineWidth + (padding * 2), totalHeight + (padding * 2));
                            ctx.fillStyle = style.color;
                        }
                        lines.forEach((line, i) => ctx.fillText(line, drawX, startY + (i * lineHeight)));
                        ctx.restore();
                    }
                }
            });
        });

        // Render Dynamic Captions (MOVED INSIDE TRANSFORM)
        if (shouldRenderCaptions) {
            const content = Array.isArray(dynamicCaptions) ? dynamicCaptions : (dynamicCaptions.content || []);
            const activeParagraph = content.find(p => time >= p.startTime && time < (p.startTime + p.duration));

            if (activeParagraph) {
                ctx.save();
                const styles = dynamicCaptions.style || dynamicCaptions.styles || {};
                const rawAppearance = dynamicCaptions.appearance || { x: 0.5, y: 0.93, scale: 1 };
                const appearance = {
                    x: rawAppearance.x > 1 ? rawAppearance.x / 720 : rawAppearance.x,
                    y: rawAppearance.y > 1 ? rawAppearance.y / 1280 : rawAppearance.y,
                    scale: rawAppearance.scale || 1,
                    maxWidth: rawAppearance.maxWidth || 0.8
                };
                if (appearance.x > 1 || appearance.x < 0) appearance.x = 0.5;

                const baseHormoziFontSize = (baseH / 720) * 35; // Use baseH instead of canvas.height for consistency
                const hormoziFontSize = baseHormoziFontSize * appearance.scale;

                ctx.font = `900 ${hormoziFontSize}px "${styles.fontFamily || 'Arial Black'}", Gadget, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';


                const spaceWidth = ctx.measureText(' ').width * 1.5;
                const canvasWidthForLayout = baseW; // Use baseW for layout consistency
                const maxAllowedWidth = (appearance.maxWidth || 0.8) * canvasWidthForLayout;

                const contentHash = (activeParagraph.words || []).map(w => w.content || '').join('|');
                const cacheKey = `${activeParagraph.startTime}_${canvasWidthForLayout}_${baseH}_${styles.fontFamily}_${appearance.scale}_${appearance.maxWidth}_${contentHash}`;
                let layout = layoutCacheRef.current[cacheKey];

                if (!layout) {
                    layoutCacheRef.current = { [cacheKey]: {} };
                    const measures = (activeParagraph.words || []).map(w => ({
                        text: (w.content || '').toUpperCase(),
                        width: ctx.measureText((w.content || '').toUpperCase()).width,
                        startTime: w.startTime,
                        duration: w.duration
                    }));

                    const wrappedLines = [];
                    let currentLine = [];
                    let currentLineWidth = 0;
                    const layoutSpaceW = ctx.measureText(' ').width * 1.5;

                    measures.forEach(m => {
                        const wordPlusSpace = m.width + layoutSpaceW;
                        if (currentLine.length > 0 && currentLineWidth + m.width > maxAllowedWidth) {
                            wrappedLines.push({ words: currentLine, width: currentLineWidth - (layoutSpaceW / 1.5) });
                            currentLine = [m];
                            currentLineWidth = wordPlusSpace;
                        } else {
                            currentLine.push(m);
                            currentLineWidth += wordPlusSpace;
                        }
                    });
                    if (currentLine.length > 0) wrappedLines.push({ words: currentLine, width: currentLineWidth - (layoutSpaceW / 1.5) });

                    layout = { lines: wrappedLines, totalHeight: wrappedLines.length * (hormoziFontSize * 1.3) };
                    layoutCacheRef.current[cacheKey] = layout;
                }

                const { lines, totalHeight: totalBoxHeight } = layout;
                const lineHeight = hormoziFontSize * 1.3;

                ctx.save();
                ctx.translate(appearance.x * baseW, appearance.y * baseH); // Use baseW/baseH
                ctx.translate(0, -totalBoxHeight / 2 + lineHeight / 2);

                lines.forEach((line, lineIdx) => {
                    ctx.save();
                    ctx.translate(0, lineIdx * lineHeight);
                    let currentX = -line.width / 2;
                    let activeWordData = null;

                    line.words.forEach((m) => {
                        const centerWordX = currentX + m.width / 2;
                        const isActiveWord = time >= m.startTime && time < (m.startTime + m.duration);
                        if (isActiveWord) activeWordData = { ...m, x: centerWordX };
                        else {
                            ctx.save();
                            ctx.translate(centerWordX, 0);
                            ctx.strokeStyle = '#000000';
                            ctx.lineWidth = hormoziFontSize / 12;
                            ctx.lineJoin = 'round';
                            ctx.strokeText(m.text, 0, 0);
                            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                            ctx.fillText(m.text, 0, 0);
                            ctx.restore();
                        }
                        currentX += m.width + spaceWidth;
                    });

                    if (activeWordData) {
                        const { text, width: wordWidth, x: wordX } = activeWordData;
                        ctx.save();
                        ctx.translate(wordX, 0);
                        ctx.scale(1.12, 1.12);
                        const highlightColor = styles.highlightBg || '#fb6b33';
                        ctx.fillStyle = highlightColor;
                        const padX = hormoziFontSize / 5;
                        const padY = hormoziFontSize / 10;
                        if (ctx.roundRect) {
                            ctx.beginPath(); ctx.roundRect(-wordWidth / 2 - padX, -hormoziFontSize / 2 - padY, wordWidth + padX * 2, hormoziFontSize + padY * 2, 6); ctx.fill();
                        } else ctx.fillRect(-wordWidth / 2 - padX, -hormoziFontSize / 2 - padY, wordWidth + padX * 2, hormoziFontSize + padY * 2);
                        ctx.strokeStyle = '#000000';
                        ctx.lineWidth = hormoziFontSize / 7;
                        ctx.lineJoin = 'round';
                        ctx.strokeText(text, 0, hormoziFontSize * 0.08);
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillText(text, 0, hormoziFontSize * 0.08);
                        ctx.restore();
                    }
                    ctx.restore();
                });
                ctx.restore();
                ctx.restore();

                // Draw Outline and Selection Handles (only if selected and NOT exporting)
                const rect = getCaptionsRectRef.current?.(activeParagraph, baseW, baseH);
                if (rect && isCaptionSelected && !isExportingRef.current) {
                    ctx.save();
                    ctx.strokeStyle = '#fb6b33';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    const pad = 15;
                    const rx = rect.left * baseW - pad; // Use baseW
                    const ry = rect.top * baseH - pad; // Use baseH
                    const rw = (rect.right - rect.left) * baseW + pad * 2;
                    const rh = (rect.bottom - rect.top) * baseH + pad * 2;
                    ctx.strokeRect(rx, ry, rw, rh);
                    ctx.setLineDash([]);
                    ctx.fillStyle = '#FFFFFF';
                    ctx.strokeStyle = '#fb6b33';
                    const handleSize = 12;
                    const handles = [{ x: rx, y: ry }, { x: rx + rw, y: ry }, { x: rx, y: ry + rh }, { x: rx + rw, y: ry + rh }, { x: rx + rw / 2, y: ry }, { x: rx + rw / 2, y: ry + rh }, { x: rx, y: ry + rh / 2 }, { x: rx + rw, y: ry + rh / 2 }];
                    handles.forEach(h => {
                        ctx.fillRect(h.x - handleSize / 2, h.y - handleSize / 2, handleSize, handleSize);
                        ctx.strokeRect(h.x - handleSize / 2, h.y - handleSize / 2, handleSize, handleSize);
                    });
                    ctx.restore();
                }
            }
        }

        ctx.restore();

        if (!isExportingRef.current && overlayRatio && overlayRatio !== aspectRatio) {
            ctx.save();

            const offset = exportCropOffsets[overlayRatio] || { x: 0, y: 0 };
            const isActive = activeCropFormat === overlayRatio;
            const { x: guideX, y: guideY, width: guideW, height: guideH } = getCropRect(canvas, overlayRatio, offset);

            // Inverted Mask Logic
            // We want to fill the area OUTSIDE the guide rect with orange
            const overlayColor = isActive ? 'rgba(249, 115, 22, 0.5)' : 'rgba(0, 0, 0, 0.7)'; // Orange if active, Dark if not

            ctx.fillStyle = overlayColor;

            // Draw 4 rectangles around the center safe area
            // 1. Top
            if (guideY > 0) {
                ctx.fillRect(0, 0, canvas.width, Math.max(0, guideY));
            }
            // 2. Bottom
            if (guideY + guideH < canvas.height) {
                ctx.fillRect(0, guideY + guideH, canvas.width, canvas.height - (guideY + guideH));
            }
            // 3. Left (between top and bottom)
            if (guideX > 0) {
                ctx.fillRect(0, guideY, Math.max(0, guideX), guideH);
            }
            // 4. Right (between top and bottom)
            if (guideX + guideW < canvas.width) {
                ctx.fillRect(guideX + guideW, guideY, canvas.width - (guideX + guideW), guideH);
            }

            // Draw Border
            ctx.strokeStyle = '#f97316';
            ctx.lineWidth = 2;
            ctx.setLineDash([10, 10]);
            ctx.strokeRect(guideX, guideY, guideW, guideH);

            // Draw Crosshair center if active
            if (isActive) {
                ctx.beginPath();
                ctx.moveTo(guideX + guideW / 2 - 10, guideY + guideH / 2);
                ctx.lineTo(guideX + guideW / 2 + 10, guideY + guideH / 2);
                ctx.moveTo(guideX + guideW / 2, guideY + guideH / 2 - 10);
                ctx.lineTo(guideX + guideW / 2, guideY + guideH / 2 + 10);
                ctx.stroke();
            }

            // Label (Hidden as per request)
            // ctx.font = 'bold 16px Arial';
            // ctx.fillStyle = isActive ? '#f97316' : 'rgba(255, 255, 255, 0.9)';
            // ctx.textAlign = 'center';
            // ctx.textBaseline = 'middle';
            // ctx.fillText(overlayRatio + (isActive ? ' (Drag to Reposition)' : ''), guideX + guideW / 2, guideY + guideH / 2);

            ctx.restore();
        }

        // Placeholder logic

        // "No Video" placeholder (only if not exporting and empty)
        const videoExists = layers.some(l =>
            l.type === 'video' && l.visible &&
            l.clips.some(c => time >= c.startTime && time < c.startTime + c.duration)
        );
        // Also treat text as video content for placeholder purposes if we just rendered some
        if (!hasVideo && !videoExists && !isExportingRef.current) {
            ctx.fillStyle = '#9ca3af'; // gray-400
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('No Video', canvas.width / 2, canvas.height / 2);
        }
    };

        let rafId;

        const loop = () => {
            if (!isExportingRef.current) {
                renderFrame(currentTime);
                rafId = requestAnimationFrame(loop);
            }
        };

        rafId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafId);
    }, [currentTime, isExporting, layers, overlayRatio, exportCropOffsets, activeCropFormat, dynamicCaptions, isCaptionSelected, selectedClipId, aspectRatio, shouldRenderCaptions]);


    // Interaction Handlers
    const getClipAtPosition = (x, y) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const ctx = canvas.getContext('2d');

        // Apply Global Crop Offset (Inverse) adjustment for Hit Testing
        // If the view is shifted, we need to shift our hit test coordinates OPPSITE?
        // No, if the view is shifted Left (-Offset), the visual content is at (Pos - Offset).
        // User clicks at MouseX.
        // We want to check if MouseX is within (Pos - Offset).
        // MouseX >= Pos - Offset  <=>  MouseX + Offset >= Pos.
        // So we adjust the Mouse Coordinates by ADDING the offset.

        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeOffset = isNativeView ? (exportCropOffsets[aspectRatio] || { x: 0, y: 0 }) : { x: 0, y: 0 };

        const testX = x + activeOffset.x;
        const testY = y + activeOffset.y;

        // Check layers in priority order (Top of Render = Index N = Front)
        // So check N, N-1, ... 0
        for (let i = layers.length - 1; i >= 0; i--) {
            const layer = layers[i];
            if (!layer.visible) continue;

            for (const clip of layer.clips) {
                const clipStart = clip.startTime;
                const clipEnd = clip.startTime + clip.duration;

                if (currentTime >= clipStart && currentTime < clipEnd) {
                    const type = (layer.type || "").toLowerCase();
                    // Use shared transform if enabled (for subtitles), otherwise use clip transform
                    const transform = (type === 'text' && layer.sharedTransform?.enabled)
                        ? layer.sharedTransform
                        : (clip.transform || { x: 0.5, y: 0.5, scale: 1 });

                    if (type === 'video' || type === 'image') {
                        const element = mediaRefs.current[clip.id];
                        if (!element) continue;

                        // Re-calculate rect in normalized coordinates (0-1)
                        const elWidth = element.videoWidth || element.naturalWidth;
                        const elHeight = element.videoHeight || element.naturalHeight;

                        if (!elWidth || !elHeight) continue;

                        const hRatio = canvasSize.width / elWidth;
                        const vRatio = canvasSize.height / elHeight;
                        const ratio = Math.max(hRatio, vRatio) * transform.scale;

                        const drawWidth = elWidth * ratio;
                        const drawHeight = elHeight * ratio;

                        // Normalized dimensions
                        const normW = drawWidth / canvasSize.width;
                        const normH = drawHeight / canvasSize.height;

                        const left = transform.x - (normW / 2);
                        const right = transform.x + (normW / 2);
                        const top = transform.y - (normH / 2);
                        const bottom = transform.y + (normH / 2);

                        if (testX >= left && testX <= right && testY >= top && testY <= bottom) {
                            return clip;
                        }
                    } else if (type === 'text') {
                        // Text hit testing
                        const useShared = layer.sharedTransform?.enabled;
                        const styleSource = (useShared && layer.sharedTransform?.style)
                            ? layer.sharedTransform.style
                            : (clip.style || {});

                        const text = clip.text || 'Text';
                        const style = {
                            fontSize: 20,
                            fontFamily: 'Arial',
                            ...styleSource
                        };

                        ctx.font = `${style.fontSize * transform.scale}px ${style.fontFamily}`;

                        // Calculate multi-line dimensions
                        const maxWidth = (transform.maxWidth || 0.9) * canvasSize.width;
                        const paragraphs = text.split('\n');
                        let lines = [];

                        paragraphs.forEach(paragraph => {
                            const words = paragraph.split(' ');
                            let currentLine = words[0];
                            for (let i = 1; i < words.length; i++) {
                                const word = words[i];
                                if (ctx.measureText(currentLine + " " + word).width < maxWidth) {
                                    currentLine += " " + word;
                                } else {
                                    lines.push(currentLine);
                                    currentLine = word;
                                }
                            }
                            lines.push(currentLine);
                        });

                        const lineHeight = style.fontSize * transform.scale * 1.2;
                        const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
                        const totalHeight = lines.length * lineHeight;

                        const textWidth = maxLineWidth + 20; // Padding
                        const textHeight = totalHeight + 20; // Padding

                        const normW = textWidth / canvasSize.width;
                        const normH = textHeight / canvasSize.height;

                        const left = transform.x - (normW / 2);
                        const right = transform.x + (normW / 2);
                        const top = transform.y - (normH / 2);
                        const bottom = transform.y + (normH / 2);

                        if (testX >= left && testX <= right && testY >= top && testY <= bottom) {
                            return clip;
                        }
                    }
                }
            }
        }
        return null;
    };

    const getCaptionsRect = (activeParagraph, optionalW = null, optionalH = null) => {
        const canvas = canvasRef.current;
        if (!canvas || !activeParagraph) return null;
        const ctx = canvas.getContext('2d');


        const calcH = optionalH || canvas.height;

        const rawAppearance = dynamicCaptions.appearance || { x: 0.5, y: 0.93, scale: 1 };

        // Normalize for hit testing if they are pixels
        const appearance = {
            x: rawAppearance.x > 1 ? rawAppearance.x / 720 : rawAppearance.x,
            y: rawAppearance.y > 1 ? rawAppearance.y / 1280 : rawAppearance.y,
            scale: rawAppearance.scale || 1
        };

        // Final safety: center if x is off-screen
        if (appearance.x > 1 || appearance.x < 0) appearance.x = 0.5;

        const baseHormoziFontSize = (calcH / 720) * 35;
        const hormoziFontSize = baseHormoziFontSize * appearance.scale;
        ctx.font = `900 ${hormoziFontSize}px "${dynamicCaptions.styles?.fontFamily || 'Arial Black'}", Gadget, sans-serif`;

        const words = activeParagraph.words || [];
        const spaceWidth = ctx.measureText(' ').width;

        const wordMeasures = words.map(w => {
            const txt = (w.content || '').toUpperCase();
            return {
                text: txt,
                width: ctx.measureText(txt).width
            };
        });

        if (wordMeasures.length === 0) return null;

        const maxAllowedWidth = (appearance.maxWidth || 0.8) * canvas.width;
        const lines = [];
        let currentLine = [];
        let currentLineWidth = 0;

        wordMeasures.forEach(m => {
            const wordPlusSpace = m.width + spaceWidth;
            if (currentLine.length > 0 && currentLineWidth + m.width > maxAllowedWidth) {
                lines.push({ width: currentLineWidth - spaceWidth });
                currentLine = [m];
                currentLineWidth = wordPlusSpace;
            } else {
                currentLine.push(m);
                currentLineWidth += wordPlusSpace;
            }
        });
        if (currentLine.length > 0) {
            lines.push({ width: currentLineWidth - spaceWidth });
        }

        const maxLineWidth = Math.max(...lines.map(l => l.width));
        const lineHeight = hormoziFontSize * 1.3;
        const totalHeight = lines.length * lineHeight;

        const normW = Math.min(0.95, (maxLineWidth + 40) / canvas.width);
        const normH = Math.min(0.95, (totalHeight + 20) / canvas.height);

        return {
            left: appearance.x - normW / 2,
            right: appearance.x + normW / 2,
            top: appearance.y - normH / 2,
            bottom: appearance.y + normH / 2,
            normW,
            normH
        };
    };
    getCaptionsRectRef.current = getCaptionsRect;

    const handleDoubleClick = (e) => {
        if (!canvasRef.current) return;

        // Disable other interactions if in Crop Reposition Mode
        if (activeCropFormat && activeCropFormat === overlayRatio) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeOffset = isNativeView ? (exportCropOffsets[aspectRatio] || { x: 0, y: 0 }) : { x: 0, y: 0 };
        const testX = x + activeOffset.x;
        const testY = y + activeOffset.y;

        const clickedClip = getClipAtPosition(x, y); // already handles offset
        if (clickedClip) {
            // Check if it's a text clip
            // Need to find layer type.
            let layerType = 'video';
            for (const l of layers) {
                if (l.clips.some(c => c.id === clickedClip.id)) {
                    layerType = (l.type || '').toLowerCase();
                    break;
                }
            }

            if (layerType === 'text' && onEditText) {
                onEditText(clickedClip);
                return;
            }
        }

        // Check if clicking on captions to open settings
        if (shouldRenderCaptions && dynamicCaptions.content) {
            const activeParagraph = dynamicCaptions.content.find(p =>
                currentTime >= p.startTime && currentTime < (p.startTime + p.duration)
            );
            const capRect = getCaptionsRect(activeParagraph);
            if (capRect && testX >= capRect.left && testX <= capRect.right && testY >= capRect.top && testY <= capRect.bottom) {
                if (onOpenCaptionSettings) onOpenCaptionSettings();
            }
        }
    };

    const handlePointerDown = (e) => {
        if (!canvasRef.current) return;

        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);

        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeOffset = isNativeView ? (exportCropOffsets[aspectRatio] || { x: 0, y: 0 }) : { x: 0, y: 0 };
        const testX = x + activeOffset.x;
        const testY = y + activeOffset.y;

        // --- CROP REDEFINITION LOGIC ---
        // If Reposition Mode is active for the current overlay
        if (activeCropFormat && activeCropFormat === overlayRatio) {
            // Global Drag for Crop Reposition
            // Allow dragging anywhere on the canvas to adjust the crop
            setIsDragging(true);
            setDragMode('crop-pan');
            setDragStart({ x, y });

            // Get current offset
            const offset = exportCropOffsets[activeCropFormat] || { x: 0, y: 0 };
            setInitialTransform(offset);
            return;
        }

        // Check if clicking on dynamic captions
        if (shouldRenderCaptions && dynamicCaptions.content) {
            const activeParagraph = dynamicCaptions.content.find(p =>
                currentTime >= p.startTime && currentTime < (p.startTime + p.duration)
            );
            const capRect = getCaptionsRect(activeParagraph);
            if (capRect) {
                const handleSize = 0.05; // 5% of canvas
                // Check corners for resizing
                const corners = [
                    { id: 'se', x: capRect.right, y: capRect.bottom },
                    { id: 'sw', x: capRect.left, y: capRect.bottom },
                    { id: 'ne', x: capRect.right, y: capRect.top },
                    { id: 'nw', x: capRect.left, y: capRect.top },
                    { id: 'n', x: (capRect.left + capRect.right) / 2, y: capRect.top },
                    { id: 's', x: (capRect.left + capRect.right) / 2, y: capRect.bottom },
                    { id: 'e', x: capRect.right, y: (capRect.top + capRect.bottom) / 2 },
                    { id: 'w', x: capRect.left, y: (capRect.top + capRect.bottom) / 2 }
                ];

                const corner = corners.find(c => Math.abs(testX - c.x) < handleSize && Math.abs(testY - c.y) < handleSize);

                if (corner) {
                    setIsDragging(true);
                    setDragMode(`resize-captions-${corner.id}`);
                    setDragStart({ x, y });

                    const rawApp = dynamicCaptions.appearance || { x: 0.5, y: 0.93, scale: 1, maxWidth: 0.8 };
                    const normInit = {
                        x: (rawApp.x > 1 ? rawApp.x / 720 : rawApp.x) || 0.5,
                        y: rawApp.y > 1 ? (rawApp.y - 20) / 1280 : rawApp.y,
                        scale: rawApp.scale || 1,
                        maxWidth: rawApp.maxWidth || 0.8
                    };
                    if (normInit.y > 0.95) normInit.y = 0.93;
                    setInitialTransform(normInit);
                    return;
                }

                if (testX >= capRect.left && testX <= capRect.right && testY >= capRect.top && testY <= capRect.bottom) {
                    // Select captions and deselect clips
                    onSelectCaption(true);
                    onSelectClip(null);

                    setIsDragging(true);
                    setDragMode('move-captions');
                    setDragStart({ x, y });
                    hasDragged.current = false;

                    const rawApp = dynamicCaptions.appearance || { x: 0.5, y: 0.93, scale: 1, maxWidth: 0.8 };
                    const normI = {
                        x: (rawApp.x > 1 ? rawApp.x / 720 : rawApp.x) || 0.5,
                        y: rawApp.y > 1 ? (rawApp.y - 20) / 1280 : rawApp.y,
                        scale: rawApp.scale || 1,
                        maxWidth: rawApp.maxWidth || 0.8
                    };
                    if (normI.y > 0.95) normI.y = 0.93;
                    setInitialTransform(normI);
                    return;
                }
            }
        }

        // CHECK CLIPS (Restored)
        const clickedClip = getClipAtPosition(x, y);
        if (clickedClip) {
            onSelectCaption(false);
            onSelectClip(clickedClip.id);
            setIsDragging(true);
            setDragMode('move');
            setDragStart({ x, y });
            setInitialTransform(clickedClip.transform || { x: 0.5, y: 0.5, scale: 1 });
        } else {
            // Deselect everything if clicking on empty space
            onSelectCaption(false);
            onSelectClip(null);
        }
    };

    const handlePointerMove = (e) => {
        if (!canvasRef.current) return;
        e.preventDefault();
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeOffset = isNativeView ? (exportCropOffsets[aspectRatio] || { x: 0, y: 0 }) : { x: 0, y: 0 };
        const testX = x + activeOffset.x;
        const testY = y + activeOffset.y;

        // Dynamic Cursors on Hover
        if (!isDragging) {
            let cursor = 'default';

            // Priority: Reposition Mode -> Captions -> Clips
            if (activeCropFormat && activeCropFormat === overlayRatio) {
                cursor = 'move';
            } else if (shouldRenderCaptions && dynamicCaptions.content && isCaptionSelected) {
                const activePar = dynamicCaptions.content.find(p =>
                    currentTime >= p.startTime && currentTime < (p.startTime + p.duration)
                );
                const capRect = getCaptionsRect(activePar);
                if (capRect) {
                    const canvas = canvasRef.current;
                    const padX = 15 / canvas.width;
                    const padY = 15 / canvas.height;

                    const hitRect = {
                        left: capRect.left - padX,
                        right: capRect.right + padX,
                        top: capRect.top - padY,
                        bottom: capRect.bottom + padY
                    };

                    const handleSizeNorm = 24 / Math.min(canvas.width, canvas.height); // Roughly match 12px handles

                    const corners = [
                        { id: 'se', x: hitRect.right, y: hitRect.bottom, c: 'nwse-resize' },
                        { id: 'sw', x: hitRect.left, y: hitRect.bottom, c: 'nesw-resize' },
                        { id: 'ne', x: hitRect.right, y: hitRect.top, c: 'nesw-resize' },
                        { id: 'nw', x: hitRect.left, y: hitRect.top, c: 'nwse-resize' },
                        { id: 'n', x: (hitRect.left + hitRect.right) / 2, y: hitRect.top, c: 'ns-resize' },
                        { id: 's', x: (hitRect.left + hitRect.right) / 2, y: hitRect.bottom, c: 'ns-resize' },
                        { id: 'e', x: hitRect.right, y: (hitRect.top + hitRect.bottom) / 2, c: 'ew-resize' },
                        { id: 'w', x: hitRect.left, y: (hitRect.top + hitRect.bottom) / 2, c: 'ew-resize' }
                    ];
                    const corner = corners.find(c => Math.abs(testX - c.x) < handleSizeNorm && Math.abs(testY - c.y) < handleSizeNorm);
                    if (corner) cursor = corner.c;
                    else if (testX >= hitRect.left && testX <= hitRect.right && testY >= hitRect.top && testY <= hitRect.bottom) cursor = 'move';
                }
            } else {
                // Check clips for generic move cursor
                const hoveredClip = getClipAtPosition(x, y);
                if (hoveredClip) cursor = 'move';
            }

            if (canvasRef.current && canvasRef.current.style.cursor !== cursor) {
                canvasRef.current.style.cursor = cursor;
            }
        }


        if (!isDragging || !dragMode) return;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        if (dragMode === 'crop-pan' && activeCropFormat) {
            const newOffset = {
                x: initialTransform.x + deltaX,
                y: initialTransform.y + deltaY
            };

            // Constrain offset so guide stays on screen?
            // Optional: for now just update
            onUpdateCropOffset(prev => ({
                ...prev,
                [activeCropFormat]: newOffset
            }));
            return;
        }

        if (dragMode === 'move-captions') {
            hasDragged.current = true;
            const newAppearance = {
                ...initialTransform,
                x: initialTransform.x + deltaX,
                y: initialTransform.y + deltaY
            };
            onUpdateDynamicCaptions({
                ...dynamicCaptions,
                appearance: newAppearance
            });
            return;
        }

        if (dragMode?.startsWith('resize-captions-')) {
            hasDragged.current = true;
            const cornerId = dragMode.replace('resize-captions-', '');

            // Calculate scale based on distance from center vs original distance
            const centerX = initialTransform.x;
            const centerY = initialTransform.y;

            let newScale = initialTransform.scale;
            let newMaxWidth = initialTransform.maxWidth || 0.8;

            // Handle specific resize directions
            if (cornerId === 'n' || cornerId === 's') {
                // Vertical drag -> Scale font (Uniform Scale based on Y)
                const startDistY = Math.abs(dragStart.y - centerY);
                const currentDistY = Math.abs(y - centerY);
                const scaleFactor = startDistY > 0.001 ? currentDistY / startDistY : 1;
                newScale = Math.max(0.1, initialTransform.scale * scaleFactor);
            } else if (cornerId === 'e' || cornerId === 'w') {
                // Horizontal drag -> Change wrapping width (MaxWidth)
                const startDistX = Math.abs(dragStart.x - centerX);
                const currentDistX = Math.abs(x - centerX);
                const widthScale = startDistX > 0.001 ? currentDistX / startDistX : 1;
                newMaxWidth = Math.max(0.2, Math.min(0.95, (initialTransform.maxWidth || 0.8) * widthScale));
            } else {
                // Diagonal drag (Corners) -> Scale both font and width logic (maintains aspect ratio roughly)
                const startDist = Math.sqrt(Math.pow(dragStart.x - centerX, 2) + Math.pow(dragStart.y - centerY, 2));
                const currentDist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                const scaleFactor = startDist > 0 ? currentDist / startDist : 1;

                // Also adjust maxWidth if dragging horizontally
                const widthScale = Math.abs(x - centerX) / Math.abs(dragStart.x - centerX);

                newScale = Math.max(0.1, initialTransform.scale * scaleFactor);
                newMaxWidth = Math.max(0.2, Math.min(0.95, (initialTransform.maxWidth || 0.8) * widthScale));
            }

            const newAppearance = {
                ...initialTransform,
                scale: newScale,
                maxWidth: newMaxWidth
            };

            onUpdateDynamicCaptions({
                ...dynamicCaptions,
                appearance: newAppearance
            });
            return;
        }

        // Find the layer and clip
        let targetLayerId, targetClip, targetLayer;
        layers.forEach(l => {
            const c = l.clips.find(c => c.id === selectedClipId);
            if (c) {
                targetLayerId = l.id;
                targetClip = c;
                targetLayer = l;
            }
        });

        if (targetClip && targetLayer) {
            // Apply Transform
            const currentTransform = targetClip.transform || { x: 0.5, y: 0.5, scale: 1 };
            let newTransform = { ...currentTransform };

            if (dragMode === 'move') {
                newTransform.x = initialTransform.x + deltaX;
                newTransform.y = initialTransform.y + deltaY;
            } else if (dragMode?.startsWith('scale')) {
                // Improved scaling logic:
                // We want to scale based on the movement relative to the CENTER of the object.
                // But for simplicity in this "center-origin" transform system, let's just use the
                // distance from center (0.5, 0.5 in normalized space if at center) relative to start.

                // NOTE: initialTransform contains the CLIP's transform at start of drag.
                // dragStart contains the mouse position at start or drag.
                // (x, y) is current mouse position.

                // Calculate distance of MOUSE from Clip Center at Start
                // Clip Center at Start = initialTransform.x, initialTransform.y
                const startDistX = dragStart.x - initialTransform.x;
                const startDistY = dragStart.y - initialTransform.y;
                const startDist = Math.sqrt(startDistX * startDistX + startDistY * startDistY);

                // Calculate distance of MOUSE from Clip Center CURRENTLY
                // Note: We assume center doesn't move during scale (center-scaling)
                const currentDistX = x - initialTransform.x;
                const currentDistY = y - initialTransform.y;
                const currentDist = Math.sqrt(currentDistX * currentDistX + currentDistY * currentDistY);

                if (startDist > 0.001) {
                    const scaleRatio = currentDist / startDist;
                    // Min 0.01 (1% of cover-fill size) so media can be made very small.
                    // Max 10 to prevent runaway upscaling.
                    newTransform.scale = Math.min(10, Math.max(0.01, initialTransform.scale * scaleRatio));
                }
            }



            // Check if Shared Transform
            const useSharedTransform = targetLayer.type === 'text' && targetLayer.sharedTransform?.enabled;

            // Store latest transform in ref (always updated, never dropped)
            pendingTransformRef.current = {
                layerId: targetLayerId,
                clipId: selectedClipId,
                transform: newTransform,
                useShared: useSharedTransform
            };

            // Schedule rAF only if not already scheduled
            if (!requestRef.current) {
                requestRef.current = requestAnimationFrame(() => {
                    const pending = pendingTransformRef.current;
                    if (pending) {
                        if (pending.useShared) {
                            onUpdateLayerTransform(pending.layerId, pending.transform);
                        } else {
                            onUpdateClip(pending.layerId, pending.clipId, { transform: pending.transform });
                        }
                        pendingTransformRef.current = null;
                    }
                    requestRef.current = null;
                });
            }
        }
    };

    const handlePointerUp = (e) => {
        if (canvasRef.current) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        if (isDragging) {
            setIsDragging(false);
            setDragMode(null);
            hasDragged.current = false;
        }
    };

    const handleContextMenu = (e) => {
        e.preventDefault();

        // Disable Context Menu in Crop Mode
        if (activeCropFormat && activeCropFormat === overlayRatio) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        // 1. Check if clicking on a Text Layer
        const clickedClip = getClipAtPosition(x, y);
        if (clickedClip) {
            // Find layer type
            let layerType = 'video';
            for (const l of layers) {
                if (l.clips.some(c => c.id === clickedClip.id)) {
                    layerType = (l.type || '').toLowerCase();
                    break;
                }
            }

            if (layerType === 'text') {
                if (onEditText) onEditText(clickedClip);
                return;
            }
        }

        if (shouldRenderCaptions && dynamicCaptions.content) {
            const activeParagraph = dynamicCaptions.content.find(p =>
                currentTime >= p.startTime && currentTime < (p.startTime + p.duration)
            );
            const capRect = getCaptionsRect(activeParagraph);
            if (capRect && x >= capRect.left && x <= capRect.right && y >= capRect.top && y <= capRect.bottom) {
                if (onOpenCaptionSettings) onOpenCaptionSettings();
            }
        }
    };

    // Context Menu State
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, clip: null });

    // Overlay for selected clip
    const renderOverlay = () => {
        if (!selectedClipId) return null;

        let selectedClip = null;
        let selectedLayer = null;
        layers.forEach(l => {
            const c = l.clips.find(c => c.id === selectedClipId);
            if (c) {
                selectedClip = c;
                selectedLayer = l;
            }
        });

        if (!selectedClip || !selectedLayer) return null;

        // Check if visible at current time
        const clipStart = selectedClip.startTime;
        const clipEnd = selectedClip.startTime + selectedClip.duration;
        if (currentTime < clipStart || currentTime >= clipEnd) return null;

        const transform = selectedClip.transform || { x: 0.5, y: 0.5, scale: 1 };
        const type = (selectedLayer.type || "").toLowerCase();

        let drawWidth, drawHeight;

        if (type === 'video' || type === 'image') {
            const element = mediaRefs.current[selectedClip.id];
            const isReady = type === 'video' ? (element && element.readyState >= 1) : (element && element.complete && element.naturalWidth > 0);

            if (!element || !isReady) return null;

            const elWidth = element.videoWidth || element.naturalWidth;
            const elHeight = element.videoHeight || element.naturalHeight;

            const hRatio = canvasSize.width / elWidth;
            const vRatio = canvasSize.height / elHeight;
            const ratio = Math.max(hRatio, vRatio) * transform.scale;

            drawWidth = elWidth * ratio;
            drawHeight = elHeight * ratio;
        } else if (type === 'text') {
            const useShared = selectedLayer.sharedTransform?.enabled;
            const styleSource = (useShared && selectedLayer.sharedTransform?.style)
                ? selectedLayer.sharedTransform.style
                : (selectedClip.style || {});

            const style = {
                fontSize: 20,
                fontFamily: 'Arial',
                ...styleSource
            };
            const text = selectedClip.text || 'Text';

            // Measure text
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const ctx = canvas.getContext('2d');
            ctx.font = `${style.fontSize * transform.scale}px ${style.fontFamily}`;

            // Calculate multi-line dimensions
            const maxWidth = (transform.maxWidth || 0.9) * canvasSize.width;
            const paragraphs = text.split('\n');
            let lines = [];

            paragraphs.forEach(paragraph => {
                const words = paragraph.split(' ');
                let currentLine = words[0];
                for (let i = 1; i < words.length; i++) {
                    const word = words[i];
                    if (ctx.measureText(currentLine + " " + word).width < maxWidth) {
                        currentLine += " " + word;
                    } else {
                        lines.push(currentLine);
                        currentLine = word;
                    }
                }
                lines.push(currentLine);
            });

            const lineHeight = style.fontSize * transform.scale * 1.2;
            const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
            const totalHeight = lines.length * lineHeight;

            drawWidth = maxLineWidth + 20; // Add padding
            drawHeight = totalHeight + 20; // Add padding
        } else {
            return null;
        }

        // Convert canvas coordinates to display coordinates
        const canvas = canvasRef.current;
        if (!canvas) return null;

        // Get actual canvas display size and position
        const canvasRect = canvas.getBoundingClientRect();

        // Calculate scale factors
        const scaleX = canvasRect.width / canvasSize.width;
        const scaleY = canvasRect.height / canvasSize.height;

        // Apply Global Crop Offset (Inverse) to Overlay Position
        // Must match renderFrame logic
        const isNativeView = !overlayRatio || isExportingRef.current || (overlayRatio === aspectRatio);
        const activeOffset = isNativeView ? (exportCropOffsets[aspectRatio] || { x: 0, y: 0 }) : { x: 0, y: 0 };

        // Calculate center position on canvas (0-1 space)
        let effectiveX = transform.x;
        let effectiveY = transform.y;

        // Apply background offset
        if (activeOffset.x !== 0 || activeOffset.y !== 0) {
            effectiveX -= activeOffset.x;
            effectiveY -= activeOffset.y;
        }

        const centerX = effectiveX * canvasSize.width;
        const centerY = effectiveY * canvasSize.height;

        // Convert to display coordinates
        const displayCenterX = centerX * scaleX;
        const displayCenterY = centerY * scaleY;
        const displayDrawWidth = drawWidth * scaleX;
        const displayDrawHeight = drawHeight * scaleY;

        // Position absolutely on screen (canvas position + offset within canvas)
        const left = canvasRect.left + displayCenterX - (displayDrawWidth / 2);
        const top = canvasRect.top + displayCenterY - (displayDrawHeight / 2);

        const handleStyle = "absolute w-3 h-3 bg-white pointer-events-auto border-2 border-orange-500 hover:scale-125 transition-transform z-[60] shadow-sm";

        const onHandleDown = (e, mode) => {
            e.stopPropagation();
            e.preventDefault();
            // Capture pointer on canvas for reliable tracking
            if (canvasRef.current) {
                canvasRef.current.setPointerCapture(e.pointerId);
            }
            setIsDragging(true);
            setDragMode(mode);
            const rect = canvasRef.current.getBoundingClientRect();
            setDragStart({
                x: (e.clientX - rect.left) / rect.width,
                y: (e.clientY - rect.top) / rect.height
            });
            setInitialTransform(selectedClip.transform || { x: 0.5, y: 0.5, scale: 1 });
        };

        return (
            <div
                className="fixed border border-orange-500/80 pointer-events-none z-50"
                style={{
                    left: left,
                    top: top,
                    width: displayDrawWidth,
                    height: displayDrawHeight,
                    boxShadow: '0 0 0 1px rgba(249, 115, 22, 0.2)'
                }}
            >
                {/* Resize Handles - 8 Points */}
                {/* Corners */}
                <div
                    className={`${handleStyle} -top-1.5 -left-1.5 cursor-nw-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-nw')}
                />
                <div
                    className={`${handleStyle} -top-1.5 -right-1.5 cursor-ne-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-ne')}
                />
                <div
                    className={`${handleStyle} -bottom-1.5 -left-1.5 cursor-sw-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-sw')}
                />
                <div
                    className={`${handleStyle} -bottom-1.5 -right-1.5 cursor-se-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-se')}
                />

                {/* Sides */}
                <div
                    className={`${handleStyle} -top-1.5 left-1/2 -translate-x-1/2 cursor-n-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-n')}
                />
                <div
                    className={`${handleStyle} -bottom-1.5 left-1/2 -translate-x-1/2 cursor-s-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-s')}
                />
                <div
                    className={`${handleStyle} top-1/2 -left-1.5 -translate-y-1/2 cursor-w-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-w')}
                />
                <div
                    className={`${handleStyle} top-1/2 -right-1.5 -translate-y-1/2 cursor-e-resize`}
                    onPointerDown={(e) => onHandleDown(e, 'scale-e')}
                />
            </div>
        );
    };

    const handleContainerClick = (e) => {
        // Only deselect if clicking the container background itself, or non-interactive areas.
        // If we click on the canvas (handled by handlePointerDown) or its handles, we don't trigger this here.
        if (e.target === containerRef.current || e.target.classList.contains('preview-container') || e.target.classList.contains('preview-canvas-wrapper')) {
            if (onSelectClip) onSelectClip(null);
            if (onSelectCaption) onSelectCaption(null);
        }
    };

    return (
        <div className="flex flex-col h-full bg-[#F9FAFB] dark:bg-[#111111]">
            <div style={{ visibility: 'hidden', height: 0, overflow: 'hidden', position: 'absolute' }}>
                {layers.map(layer => (
                    layer.clips.map(clip => {
                        const type = (layer.type || "").toLowerCase();
                        if (type === 'video') {
                            return (
                                <video
                                    key={clip.id}
                                    ref={el => {
                                        if (el) mediaRefs.current[clip.id] = el;
                                        else delete mediaRefs.current[clip.id];
                                    }}
                                    src={clip.mediaFile.url}
                                    preload="auto"
                                    muted={layer.muted}
                                    crossOrigin="anonymous"
                                />
                            );
                        } else if (type === 'audio') {
                            return (
                                <audio
                                    key={clip.id}
                                    ref={el => {
                                        if (el) mediaRefs.current[clip.id] = el;
                                        else delete mediaRefs.current[clip.id];
                                    }}
                                    src={clip.mediaFile.url}
                                    preload="auto"
                                    muted={layer.muted}
                                />
                            );
                        } else if (type === 'image') {
                            return (
                                <img
                                    key={clip.id}
                                    ref={el => {
                                        if (el) mediaRefs.current[clip.id] = el;
                                        else delete mediaRefs.current[clip.id];
                                    }}
                                    src={clip.mediaFile.url}
                                    crossOrigin="anonymous"
                                    alt=""
                                />
                            );
                        }
                        return null;
                    })
                ))}
            </div>

            <div
                ref={containerRef}
                className="flex-1 relative flex items-center justify-center overflow-hidden py-8 px-4 group preview-container"
                onClick={handleContainerClick}
            >
                <div
                    className="relative shadow-2xl transition-all duration-300 pointer-events-auto preview-canvas-wrapper"
                    style={{
                        width: `${displaySize.width}px`,
                        height: `${displaySize.height}px`,
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        width={canvasSize.width}
                        height={canvasSize.height}
                        className="max-w-full max-h-full block shadow-2xl dark:border-white/10 rounded-2xl overflow-hidden"
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                        onContextMenu={handleContextMenu}
                        onDoubleClick={handleDoubleClick}
                        style={{
                            aspectRatio: `${canvasSize.width} / ${canvasSize.height}`,
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            imageRendering: 'crisp-edges'
                        }}
                    />
                    {!isExportingRef.current && renderOverlay()}
                </div>
            </div>

            {(isExporting || isExportingMode) && (
                <div className="absolute inset-0 bg-dark-900/90 flex flex-col items-center justify-center z-[100] backdrop-blur-md">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mb-4 shadow-[0_0_20px_rgba(249,115,22,0.5)]"></div>
                    <h3 className="text-xl font-bold text-white mb-2">Exporting your video...</h3>
                    <p className="text-white text-sm italic">{exportMessage || 'Your export will download when it is ready.'}</p>
                    <div className="w-64 bg-white/20 rounded-full h-2 overflow-hidden mt-4">
                        <div className="bg-orange-500 h-full transition-all duration-300 rounded-full" style={{ width: `${exportProgress}%` }} />
                    </div>
                </div>
            )}

            {isLoading && !isExporting && (
                <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-[100]">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-4"></div>
                    <h3 className="text-lg font-bold text-white mb-2">{loadingMessage || "Loading assets..."}</h3>
                    <div className="w-64 bg-white/20 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-orange-500 h-full transition-all duration-300 rounded-full"
                            style={{ width: `${(loadingProgress || 0) * 100}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{Math.round((loadingProgress || 0) * 100)}%</p>
                </div>
            )}

            {dragMode === 'crop-pan' && (
                <div className="absolute inset-0 z-50 cursor-grabbing bg-black/10" />
            )}

            {contextMenu.show && (
                <LayerContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={() => setContextMenu({ ...contextMenu, show: false })}
                    actions={[
                        {
                            label: 'Edit Text',
                            icon: <Edit className="w-4 h-4" />,
                            onClick: () => {
                                if (onEditText && contextMenu.clip) {
                                    onEditText(contextMenu.clip);
                                }
                            }
                        }
                    ]}
                />
            )}
        </div>
    );
});

export default VideoPreview;
